import React from "react";
import { Box, Grid2 } from "@mui/material";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import type {
  Dashboard2DayBookingSummary,
  DayWorkStatus,
  DayWorkStatusKind
} from "../types/dashboard2-work-status.types";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import {
  dashboard2TopRowQuickActionsCellSx,
  dashboard2TopRowQuickActionsCellContentSx,
  dashboard2TopRowTodayCellContentSx,
  dashboard2TopRowTodayCellSx
} from "../dashboard2-status.styles";
import {
  dashboard2TodayQuickActionsRowSx,
  dashboard2TopRowStackedCellContentSx,
  DASHBOARD2_TOP_ROW_BREAKPOINT_PX
} from "../dashboard2.styles";
import { useElementWidth } from "../hooks/useElementWidth";
import { Dashboard2BookingQuickActionsPanel } from "./Dashboard2BookingQuickActionsPanel";
import { Dashboard2TopRowColleaguesCell } from "./Dashboard2TopRowColleaguesCell";
import { Dashboard2TodayStatusStrip } from "./Dashboard2TodayStatusStrip";

type Dashboard2TopRowProps = {
  todayStatus: DayWorkStatus;
  primaryTodaySchedule?: BookingScheduleInterface;
  allSchedules?: BookingScheduleInterface[];
  refetchSchedules?: () => void;
  onClickUnlock?: () => void;
  onSelectTodayStatus?: (kind: DayWorkStatusKind) => void;
  onBookingClick?: (booking: Dashboard2DayBookingSummary) => void;
  onPrimaryScheduleClick?: (schedule: BookingScheduleInterface) => void;
  onOpenFloorPlan?: (schedule?: BookingScheduleInterface) => void;
  onBookAVisitor?: () => void;
  onBookPlaceType?: (bookingType: BookingType) => void;
  placeBookingTypes?: readonly BookingType[];
  colleagueSchedules: BookingScheduleInterface[];
  storedColleagues: TeamMember[];
  visitorManagementEnabled: boolean;
  conferenceZoneBookingEnabled: boolean;
  mobileWorkingEnabled?: boolean;
  schedulesLoading?: boolean;
  statusActionsDisabled?: boolean;
  onShowColleaguesField?: () => void;
};

export const Dashboard2TopRow: React.FC<Dashboard2TopRowProps> = ({
  todayStatus,
  primaryTodaySchedule,
  allSchedules,
  refetchSchedules,
  onClickUnlock,
  onSelectTodayStatus,
  onBookingClick,
  onPrimaryScheduleClick,
  onOpenFloorPlan,
  onBookAVisitor,
  onBookPlaceType,
  placeBookingTypes = [],
  colleagueSchedules,
  storedColleagues,
  visitorManagementEnabled,
  conferenceZoneBookingEnabled,
  mobileWorkingEnabled = true,
  schedulesLoading = false,
  statusActionsDisabled = false,
  onShowColleaguesField
}) => {
  const [layoutMeasureRef, layoutWidth] = useElementWidth();
  const isWideRow = layoutWidth >= DASHBOARD2_TOP_ROW_BREAKPOINT_PX;

  const handleSelectTodayStatus = (kind: DayWorkStatusKind) => {
    onSelectTodayStatus?.(kind);
  };

  const stackedCellSize = { xs: 12 } as const;
  const stackedCellContentSx = dashboard2TopRowStackedCellContentSx;

  return (
    <Box data-testid="dashboard2-top-row">
      <Box
        ref={layoutMeasureRef}
        sx={{ width: "100%", contain: "inline-size" }}
        data-testid="dashboard2-top-row-layout"
        data-layout={isWideRow ? "wide" : "stacked"}
      >
        <Grid2
          container
          spacing={1.5}
          sx={{
            ...(dashboard2TodayQuickActionsRowSx as object),
            mb: 2
          }}
          data-testid="dashboard2-top-grid"
        >
          <Grid2
            size={isWideRow ? "grow" : stackedCellSize}
            sx={dashboard2TopRowTodayCellSx(isWideRow)}
          >
            <Box sx={isWideRow ? dashboard2TopRowTodayCellContentSx : stackedCellContentSx}>
              <Dashboard2TodayStatusStrip
                status={todayStatus}
                primarySchedule={primaryTodaySchedule}
                allSchedules={allSchedules}
                refetchSchedules={refetchSchedules}
                onClickUnlock={onClickUnlock}
                onSelectStatus={handleSelectTodayStatus}
                onBookingClick={onBookingClick}
                onPrimaryScheduleClick={onPrimaryScheduleClick}
                onOpenFloorPlan={onOpenFloorPlan}
                mobileWorkingEnabled={mobileWorkingEnabled}
                isLoading={schedulesLoading}
                statusActionsDisabled={statusActionsDisabled}
              />
            </Box>
          </Grid2>

          <Dashboard2TopRowColleaguesCell
            schedules={colleagueSchedules}
            storedColleagues={storedColleagues}
            isLoading={schedulesLoading}
            onShowColleaguesField={onShowColleaguesField}
            isWideRow={isWideRow}
            stackedCellSize={stackedCellSize}
          />

          <Grid2
            size={isWideRow ? "auto" : 12}
            sx={dashboard2TopRowQuickActionsCellSx(isWideRow)}
            data-testid="dashboard2-top-row-quick-actions-cell"
          >
            <Box sx={dashboard2TopRowQuickActionsCellContentSx(isWideRow)}>
              <Dashboard2BookingQuickActionsPanel
                placeBookingTypes={placeBookingTypes}
                visitorManagementEnabled={visitorManagementEnabled}
                conferenceZoneBookingEnabled={conferenceZoneBookingEnabled}
                onBookPlaceType={onBookPlaceType}
                onBookAVisitor={onBookAVisitor}
                isWideRow={isWideRow}
                actionsDisabled={statusActionsDisabled || schedulesLoading}
              />
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};
